import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Siren } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { api, formatApiError } from "@/lib/api";

/**
 * Reusable SOS emergency dialog. Raises an escalation, flags an ambulance,
 * fans out critical notifications, and optionally starts an emergency teleconsult.
 * `context` = { case_type, case_id, patient_id, patient_name }.
 */
export default function SosDialog({ open, onOpenChange, context, onDone }) {
  const navigate = useNavigate();
  const [reason, setReason] = useState("");
  const [ambulance, setAmbulance] = useState(true);
  const [teleconsult, setTeleconsult] = useState(false);
  const [busy, setBusy] = useState(false);

  if (!context) return null;

  function reset() { setReason(""); setAmbulance(true); setTeleconsult(false); }

  async function fire() {
    setBusy(true);
    try {
      const res = await api.post("/critical/sos", {
        case_type: context.case_type || "manual",
        case_id: context.case_id || null,
        patient_id: context.patient_id,
        reason: reason || null,
        request_ambulance: ambulance,
        start_teleconsult: teleconsult,
      });
      toast.success(`SOS raised — ${res.data.recipients_notified} clinician(s) alerted${ambulance ? " · ambulance flagged" : ""}`);
      let roomUrl = null;
      if (teleconsult) {
        try {
          const tc = await api.post("/teleconsult/consultations", {
            patient_id: context.patient_id, consultation_type: "follow_up",
            priority: "critical", duration_minutes: 30, source_module: "manual",
            notes: `Emergency teleconsult raised via SOS. ${reason || ""}`.trim(),
          });
          roomUrl = `/teleconsult/room/${tc.data.id}`;
        } catch (e) {
          toast.error("SOS sent, but the teleconsult could not be created: " + formatApiError(e));
        }
      }
      onOpenChange(false);
      reset();
      onDone && onDone(res.data);
      if (roomUrl) navigate(roomUrl);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!busy) onOpenChange(o); }}>
      <DialogContent className="max-w-md" data-testid="sos-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-[#B91C1C]"><Siren className="w-5 h-5" /> Raise SOS</DialogTitle>
          <DialogDescription>
            Emergency escalation for <b>{context.patient_name || "this patient"}</b>. The on-call physician and H@H Care Coordinators are alerted immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Textarea rows={3} placeholder="What's happening? (clinical reason)" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="sos-reason" />
          <label className="flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
            <Checkbox checked={ambulance} onCheckedChange={(v) => setAmbulance(!!v)} data-testid="sos-ambulance" />
            Request ambulance / 997 dispatch
          </label>
          <label className="flex items-center gap-2 text-sm text-[#1F2937] cursor-pointer">
            <Checkbox checked={teleconsult} onCheckedChange={(v) => setTeleconsult(!!v)} data-testid="sos-teleconsult" />
            Start emergency teleconsult now
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-[#E5E7EB]" disabled={busy}>Cancel</Button>
          <Button onClick={fire} disabled={busy} className="bg-[#B91C1C] hover:bg-[#991B1B] text-white" data-testid="sos-confirm">
            {busy ? "Sending…" : <><Siren className="w-4 h-4 mr-1.5" />{teleconsult ? "Raise SOS & Join" : "Raise SOS"}</>}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
